import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import type { CourseManifest } from "@/lib/course";
import type { ProgressRecord } from "@/lib/progress";
import { isCompleted, coursePercent } from "@/lib/progress";
import { isLessonUnlocked } from "@/lib/navigation";
import { cn } from "@/lib/cn";
import { useChannel, withChannel } from "@/lib/channel";
import { CheckIcon, CloseIcon } from "@/components/icons";

// A bare checkmark would otherwise be the only signal a lesson/quiz is
// complete — aria-hidden the icon but keep an sr-only affordance so screen
// readers still hear it.
function lessonMarker(done: boolean, notDoneLabel: string): JSX.Element {
  if (!done) return <>{notDoneLabel}</>;
  return (
    <>
      <CheckIcon className="h-3.5 w-3.5 inline-block" />
      <span className="sr-only">Completed</span>
    </>
  );
}

interface Props {
  open: boolean;
  onClose: () => void;
  manifest: CourseManifest;
  progress: ProgressRecord | null;
  courseSlug: string;
  currentChapterFolder: string;
  currentLessonSlug: string;
}

export function CourseOutlineDrawer({
  open,
  onClose,
  manifest,
  progress,
  courseSlug,
  currentChapterFolder,
  currentLessonSlug
}: Props): JSX.Element {
  const channel = useChannel();
  // Esc to close
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent): void => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [open, onClose]);

  // Scroll the current chapter into view when the drawer opens. In a long
  // course (20+ chapters), the student's current spot would otherwise be
  // lost in the middle of the list.
  const currentChapterRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!open) return;
    // Defer to next frame so the drawer's transition has started and the
    // element is in the layout tree.
    const id = requestAnimationFrame(() => {
      currentChapterRef.current?.scrollIntoView({ block: "center", behavior: "smooth" });
    });
    return () => cancelAnimationFrame(id);
  }, [open]);

  return (
    <>
      <div
        className={cn(
          "fixed inset-0 z-30 bg-foreground/30 transition-opacity duration-200",
          open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        )}
        onClick={onClose}
        aria-hidden
      />
      <aside
        className={cn(
          "fixed top-0 left-0 z-40 w-[88vw] max-w-sm h-screen bg-card border-r border-border p-4 shadow-xl overflow-y-auto transition-transform duration-200",
          open ? "translate-x-0" : "-translate-x-full"
        )}
        role="dialog"
        aria-modal="true"
        aria-label="Course outline"
      >
        <div className={cn(open && "cell-rise")}>
          <div className="flex items-center justify-between mb-3 pb-2 border-b border-border-subtle">
            <h4 className="m-0 text-[13px] uppercase tracking-wider font-semibold">Course outline</h4>
            <button
              onClick={onClose}
              className="bg-transparent border-0 text-muted-foreground cursor-pointer text-base px-2 rounded hover:bg-muted"
              aria-label="Close outline"
            >
              <CloseIcon className="h-4 w-4" />
            </button>
          </div>

          {/* Course overview — title, blurb, counts, progress bar */}
          <div className="mb-4 pb-4 border-b border-border">
            <div className="text-[10px] tracking-wide text-primary-soft font-bold mb-1">
              Course
            </div>
            <h3 className="text-base font-semibold leading-tight mb-1">{manifest.title}</h3>
            <p className="text-xs text-muted-foreground leading-snug mb-2.5">{manifest.summary}</p>
            <div className="text-[11px] text-muted-foreground mb-2.5">
              {manifest.chapters.length} chapters · {manifest.chapters.reduce((a, c) => a + c.lessons.length, 0)} lessons · ~{manifest.estimatedHours}h
            </div>
            {(() => {
              const total = manifest.chapters.reduce((a, c) => a + c.lessons.length, 0);
              const percent = progress ? coursePercent(progress, total) : 0;
              const done = progress?.completedLessons.length ?? 0;
              return (
                <>
                  <div className="h-1.5 rounded-full bg-muted overflow-hidden mb-1.5">
                    <div
                      className="h-full bg-gradient-to-r from-primary to-primary-soft transition-all"
                      style={{ width: `${percent}%` }}
                    />
                  </div>
                  <div className="text-[11px] text-muted-foreground">
                    {percent}% · {done} of {total} lessons
                  </div>
                </>
              );
            })()}
          </div>

          {manifest.chapters.map((chapter, chapterIdx) => {
            const chapterIsCurrent = chapter.folder === currentChapterFolder;
            const chapterIsDone = chapter.lessons.every((l) =>
              progress ? isCompleted(progress, chapter.folder, l.slug) : false
            );
            return (
              <div
                key={chapter.folder}
                ref={chapterIsCurrent ? currentChapterRef : null}
                className={cn(
                  chapterIsCurrent && "bg-cell-overview/10 border border-cell-overview-border/40 rounded-control p-2 -mx-2 mt-4 shadow-sm"
                )}
              >
                <div
                  className={cn(
                    "flex items-center justify-between mb-2 font-bold",
                    chapterIsCurrent
                      ? "text-[12px] text-primary tracking-tight"
                      : "text-[10px] tracking-wider uppercase",
                    chapterIdx === 0
                      ? ""
                      : !chapterIsCurrent && "mt-4 pt-1 border-t border-border-subtle",
                    chapterIsDone && !chapterIsCurrent && "text-success uppercase"
                  )}
                >
                  <span>Chapter {chapter.chapterNumber}: {chapter.title}</span>
                  {chapterIsCurrent && (
                    <span className="text-[10px] tracking-wide px-2 py-0.5 rounded bg-primary text-primary-foreground">
                      Now
                    </span>
                  )}
                  {!chapterIsCurrent && chapterIsDone && (
                    <span className="text-[10px] tracking-wide px-1.5 py-px rounded bg-success text-white">
                      Done
                    </span>
                  )}
                </div>
                {chapter.lessons.map((lesson, idx) => {
                  const isCurrent =
                    chapter.folder === currentChapterFolder && lesson.slug === currentLessonSlug;
                  const done = progress
                    ? isCompleted(progress, chapter.folder, lesson.slug)
                    : false;
                  const unlocked = isLessonUnlocked(manifest, progress, chapter.folder, lesson.slug);
                  const marker = lessonMarker(done, `${idx + 1}.`);

                  if (!unlocked) {
                    return (
                      <span
                        key={lesson.slug}
                        className="flex items-center gap-2 px-2 py-0.5 rounded text-[11px] text-subtle-foreground leading-snug"
                      >
                        <span className="w-[18px] text-left text-subtle-foreground">{marker}</span>
                        {lesson.title}
                      </span>
                    );
                  }
                  if (isCurrent) {
                    return (
                      <span
                        key={lesson.slug}
                        aria-current="page"
                        className="flex items-center gap-2 px-1.5 py-0.5 bg-cell-overview/10 hover:bg-cell-overview/20 text-primary font-semibold border-l-[3px] border-cell-overview-border rounded-r text-[11px] cursor-pointer leading-snug"
                      >
                        <span className="w-[18px] text-left">{marker}</span>
                        {lesson.title}
                      </span>
                    );
                  }
                  return (
                    <Link
                      key={lesson.slug}
                      to={withChannel(`/courses/${courseSlug}/${chapter.folder}/${lesson.slug}`, channel)}
                      className={cn(
                        "flex items-center gap-2 px-2 py-0.5 rounded text-[11px] leading-snug hover:bg-muted",
                        done ? "text-muted-foreground" : "text-foreground"
                      )}
                    >
                      <span
                        className={cn(
                          "w-[18px] text-left",
                          done ? "text-success font-bold" : "text-muted-foreground"
                        )}
                      >
                        {marker}
                      </span>
                      {lesson.title}
                    </Link>
                  );
                })}
                {/* Chapter-quiz pseudo-lesson — appears after all real lessons */}
                {chapter.chapterQuiz && (() => {
                  const isCurrent =
                    chapter.folder === currentChapterFolder &&
                    currentLessonSlug === "chapter-quiz";
                  const done = progress
                    ? isCompleted(progress, chapter.folder, "chapter-quiz")
                    : false;
                  const unlocked = isLessonUnlocked(
                    manifest,
                    progress,
                    chapter.folder,
                    "chapter-quiz"
                  );
                  const quizMarker = lessonMarker(done, "Q.");

                  if (!unlocked) {
                    return (
                      <span
                        key="chapter-quiz"
                        className="flex items-center gap-2 px-2 py-0.5 rounded text-[11px] text-subtle-foreground leading-snug mt-0.5 border-t border-border-subtle/50 pt-1"
                      >
                        <span className="w-[18px] text-left text-subtle-foreground">{quizMarker}</span>
                        <span className="italic">{chapter.chapterQuiz.title}</span>
                      </span>
                    );
                  }
                  if (isCurrent) {
                    return (
                      <span
                        key="chapter-quiz"
                        aria-current="page"
                        className="flex items-center gap-2 px-1.5 py-0.5 bg-cell-overview/10 hover:bg-cell-overview/20 text-primary font-semibold border-l-[3px] border-cell-overview-border rounded-r text-[11px] cursor-pointer leading-snug mt-0.5 border-t border-border-subtle/50 pt-1"
                      >
                        <span className="w-[18px] text-left">{quizMarker}</span>
                        <span className="italic">{chapter.chapterQuiz.title}</span>
                      </span>
                    );
                  }
                  return (
                    <Link
                      key="chapter-quiz"
                      to={withChannel(`/courses/${courseSlug}/${chapter.folder}/chapter-quiz`, channel)}
                      className={cn(
                        "flex items-center gap-2 px-2 py-0.5 rounded text-[11px] leading-snug hover:bg-muted mt-0.5 border-t border-border-subtle/50 pt-1",
                        done ? "text-muted-foreground" : "text-foreground"
                      )}
                    >
                      <span
                        className={cn(
                          "w-[18px] text-left font-semibold",
                          done ? "text-success font-bold" : "text-muted-foreground"
                        )}
                      >
                        {quizMarker}
                      </span>
                      <span className="italic">{chapter.chapterQuiz.title}</span>
                    </Link>
                  );
                })()}
              </div>
            );
          })}

          {/* Final exam row — only when the course defines one */}
          {manifest.finalExam !== undefined && (() => {
            const isCurrent = currentLessonSlug === "final-exam";
            const examMarker = "FE";
            const examTitle = manifest.finalExam.title;
            if (isCurrent) {
              return (
                <span
                  key="final-exam"
                  aria-current="page"
                  className="flex items-center gap-2 px-1.5 py-0.5 bg-cell-overview/10 hover:bg-cell-overview/20 text-primary font-semibold border-l-[3px] border-cell-overview-border rounded-r text-[11px] cursor-pointer leading-snug mt-4 pt-3 border-t border-border"
                >
                  <span className="w-[18px] text-left text-xs">{examMarker}</span>
                  <span className="italic">{examTitle}</span>
                </span>
              );
            }
            return (
              <Link
                key="final-exam"
                to={withChannel(`/courses/${courseSlug}/final-exam`, channel)}
                className="flex items-center gap-2 px-2 py-0.5 rounded text-[11px] leading-snug hover:bg-muted text-foreground mt-4 pt-3 border-t border-border"
              >
                <span className="w-[18px] text-left text-xs font-semibold text-muted-foreground">
                  {examMarker}
                </span>
                <span className="italic">{examTitle}</span>
              </Link>
            );
          })()}
        </div>
      </aside>
    </>
  );
}
